import React from 'react';
import {
  Grid,
  Paper,
  Typography,
  Chip,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTheme } from '@mui/material/styles';
import { TFunction } from 'i18next';
import { formatMoney } from 'utils/helpers';
import { AccountingType } from 'v2-types/order';

const useStyles = makeStyles((theme: any) => ({
  paperContainer: {
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: theme.spacing(1),
    margin: `${theme.spacing(2.75)} ${theme.spacing(2.5)}`,
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0),
    },
  },
  contentContainer: {
    padding: theme.spacing(4),
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      gap: theme.spacing(1),
    },
  },
  title: {
    fontWeight: 'bold',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  flowsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: theme.spacing(2),
    },
  },
  issuesList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    alignContent: 'flex-start',
  },
  noIssues: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  flowGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  flowTitle: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  stats: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  statValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  statLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
  },
  subValue: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    fontSize: '0.85em',
  },
}));

type SettlementHeaderProps = {
  accounting: AccountingType[];
  t: TFunction;
  orderId?: string;
};

/**
 * Helper: Calculate percentage with zero guard and capping at 100.
 */
const getPercentage = (accounted: number, total: number): number => {
  if (!Number.isFinite(accounted) || !Number.isFinite(total) || total <= 0) return 0;
  const pct = (accounted / total) * 100;
  if (!Number.isFinite(pct)) return 0;
  return Math.min(100, Math.max(0, Number(pct.toFixed(2))));
};

export const SettlementHeader: React.FC<SettlementHeaderProps> = ({
  accounting,
  t,
  orderId,
}) => {
  const classes = useStyles();
  const theme = useTheme();

  /**
   * Completion-aware chip color.
   * Green only when accounted amount exactly equals total (to the cent).
   * Warning for partial progress (>= 50%). Error for below 50%.
   */
  const getCompletionColor = (accounted: number, total: number): string => {
    if (total <= 0) return theme.palette.error.main;
    const accCents = Math.round(accounted * 100);
    const totCents = Math.round(total * 100);
    if (accCents === totCents) return theme.palette.success.main;
    if ((accounted / total) * 100 >= 50) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  // Classify and sum at party-association level.
  // Entries must have valid letter + number; exclude letter J entirely.
  const hasValidId = (e: AccountingType) =>
    typeof e.letter === 'string' && e.letter.trim() !== '' && e.number != null;

  let disbursementTotal = 0;
  let disbursementAccountedSum = 0;
  const disbursementEntryIds = new Set<string>();
  const disbursementAccountedEntryIds = new Set<string>();

  let receiptTotal = 0;
  let receiptAccountedSum = 0;
  const receiptEntryIds = new Set<string>();
  const receiptAccountedEntryIds = new Set<string>();

  const validEntries = accounting.filter(hasValidId).filter(
    (entry) => (entry.letter as string)?.trim()?.toUpperCase() !== 'J'
  );

  validEntries.forEach((entry) => {
    (entry.involved_parties || []).forEach((p) => {
      if (p.kind === 'Payee') {
        disbursementTotal += p.amount || 0;
        const acc = p.accounted_amount || 0;
        disbursementAccountedSum += acc;
        disbursementEntryIds.add(entry._id);
        if (acc > 0) disbursementAccountedEntryIds.add(entry._id);
      }
      if (p.kind === 'Payer') {
        receiptTotal += p.amount || 0;
        const acc = p.accounted_amount || 0;
        receiptAccountedSum += acc;
        receiptEntryIds.add(entry._id);
        if (acc > 0) receiptAccountedEntryIds.add(entry._id);
      }
    });
  });

  const disbursementCount = disbursementEntryIds.size;
  const disbursementAccountedCount = disbursementAccountedEntryIds.size;
  const disbursementPercent = getPercentage(disbursementAccountedSum, disbursementTotal);

  const receiptCount = receiptEntryIds.size;
  const receiptAccountedCount = receiptAccountedEntryIds.size;
  const receiptPercent = getPercentage(receiptAccountedSum, receiptTotal);

  const getEffectiveAmount = (entry: AccountingType): number => {
    if (entry.amount_override !== 0) return entry.amount_override;
    if (entry.amount_calculated !== 0) return entry.amount_calculated;
    return entry.amount;
  };

  const isBalanced = (entry: AccountingType): boolean => {
    const payers = entry.involved_parties?.filter((p: any) => p.kind === 'Payer' && (p.amount || 0) > 0) || [];
    const payees = entry.involved_parties?.filter((p: any) => p.kind === 'Payee' && (p.amount || 0) > 0) || [];
    if (payers.length === 0 || payees.length === 0) return false;
    const payersAmount = payers.reduce((sum: number, p: any) => sum + (p.accounted_amount || 0), 0);
    const payeesAmount = payees.reduce((sum: number, p: any) => sum + (p.accounted_amount || 0), 0);
    const effectiveAmount = getEffectiveAmount(entry);
    if (Math.abs(payersAmount - payeesAmount) > 0.01) return false;
    if (Math.abs(payersAmount - effectiveAmount) > 0.01) return false;
    if (Math.abs(payeesAmount - effectiveAmount) > 0.01) return false;
    return true;
  };

  const issueEntries = accounting.filter((entry) => !isBalanced(entry));

  return (
    <Paper className={classes.paperContainer}>
      <Grid container className={classes.contentContainer} direction="column">
        <Typography variant="h6" className={classes.title}>
          {t('settlement:header')}
        </Typography>
        <Grid container className={classes.flowsContainer}>
          {/* DISBURSEMENT GROUP */}
          <Grid item className={classes.flowGroup}>
            <Typography className={classes.flowTitle}>
              {t('settlement:disbursement-flow', 'Disbursement')}
            </Typography>
            <Grid container className={classes.stats}>
              <Grid item className={classes.statCard}>
                <Typography className={classes.statValue}>
                  {formatMoney(disbursementTotal, 2)}
                </Typography>
                <Typography className={classes.statLabel}>
                  {t('settlement:total-amount', 'Total Amount')}
                </Typography>
              </Grid>

              <Grid item className={classes.statCard}>
                <div>
                  <Typography className={classes.statValue}>{disbursementCount}</Typography>
                  <Box component="span" className={classes.subValue} style={{ marginLeft: '8px' }}>
                    <Chip label={t('settlement:accounted-count', { count: disbursementAccountedCount })} size="small" />
                  </Box>
                  <Typography className={classes.statLabel}>
                    {t('settlement:entries-count', 'Total Amount')}
                  </Typography>
                </div>
              </Grid>

              <Grid item className={classes.statCard}>
                <div>
                  <Typography className={classes.statValue}>
                    {formatMoney(disbursementAccountedSum, 2)}
                  </Typography>
                  <Box component="span" className={classes.subValue} style={{ marginLeft: '8px' }}>
                    <Chip
                      label={`${disbursementPercent.toFixed(2)}%`}
                      size="small"
                      style={{ backgroundColor: getCompletionColor(disbursementAccountedSum, disbursementTotal), color: '#fff' }}
                    />
                  </Box>
                  <Typography className={classes.statLabel}>
                    {t('settlement:accounted-sum', 'Accounted Sum')}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </Grid>

          {/* RECEIPT GROUP */}
          <Grid item className={classes.flowGroup}>
            <Typography className={classes.flowTitle}>
              {t('settlement:receipt-flow', 'Receipt')}
            </Typography>
            <Grid container className={classes.stats}>
              <Grid item className={classes.statCard}>
                <Typography className={classes.statValue}>
                  {formatMoney(receiptTotal, 2)}
                </Typography>
                <Typography className={classes.statLabel}>
                  {t('settlement:total-amount', 'Total Amount')}
                </Typography>
              </Grid>

              <Grid item className={classes.statCard}>
                <div>
                  <Typography className={classes.statValue}>{receiptCount}</Typography>
                  <Box component="span" className={classes.subValue} style={{ marginLeft: '8px' }}>
                    <Chip label={t('settlement:accounted-count', { count: receiptAccountedCount })} size="small" />
                  </Box>
                  <Typography className={classes.statLabel}>
                    {t('settlement:entries-count', 'Entries')}
                  </Typography>
                </div>
              </Grid>

              <Grid item className={classes.statCard}>
                <div>
                  <Typography className={classes.statValue}>{formatMoney(receiptAccountedSum, 2)}</Typography>
                  <Box component="span" className={classes.subValue} style={{ marginLeft: '8px' }}>
                    <Chip
                      label={`${receiptPercent.toFixed(2)}%`}
                      size="small"
                      style={{ backgroundColor: getCompletionColor(receiptAccountedSum, receiptTotal), color: '#fff' }}
                    />
                  </Box>
                  <Typography className={classes.statLabel}>
                    {t('settlement:accounted-sum', 'Accounted Sum')}
                  </Typography>
                </div>
              </Grid>
            </Grid>
          </Grid>

          {/* ISSUES GROUP */}
          <Grid item className={classes.flowGroup}>
            <Typography className={classes.flowTitle}>
              {t('settlement:issues', 'Issues')}
              {' '}
              (
              {issueEntries.length}
              )
            </Typography>
            <Box className={classes.issuesList}>
              {issueEntries.length === 0 ? (
                <Typography className={classes.noIssues}>
                  {t('settlement:no-issues', 'No issues')}
                </Typography>
              ) : (
                issueEntries.map((entry) => (
                  <Chip
                    key={entry._id}
                    label={hasValidId(entry) ? `${entry.letter}-${entry.number}` : entry.description}
                    size="small"
                    style={{ backgroundColor: theme.palette.error.main, color: '#fff' }}
                  />
                ))
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};
